import { ServiceQuoteInput, WebsiteOnboardingInput } from './dbService';

// Helper to escape HTML characters safely
function escapeHtml(str: string | undefined | null): string {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export interface EmailTemplateConfig {
  brandMark: string;
  brandName: string;
  brandSubtitle: string;
  accentColor: string;
  accentGradientEnd: string;
  backgroundColor: string;
  cardBackground: string;
  supportEmail: string;
  supportPhone: string;
  locationText: string;
  customFooterNote: string;
  showSignatureCard: boolean;
  ctaButtonTextQuote: string;
  ctaButtonTextOnboarding: string;
  customGreetingQuote?: string;
  customGreetingOnboarding?: string;
}

export const DEFAULT_EMAIL_CONFIG: EmailTemplateConfig = {
  brandMark: 'RB',
  brandName: 'Randon Brown, LLC',
  brandSubtitle: 'IT Leader • Technology • Websites',
  accentColor: '#f59e0b',
  accentGradientEnd: '#d97706',
  backgroundColor: '#070709',
  cardBackground: '#0d1117',
  supportEmail: 'help@randonbrown.com',
  supportPhone: '(314) 944-3074',
  locationText: 'Texas County • Licking, Missouri, United States',
  customFooterNote: '',
  showSignatureCard: true,
  ctaButtonTextQuote: 'Reply Directly to Client',
  ctaButtonTextOnboarding: 'Reply Directly to Client',
  customGreetingQuote: 'Thank you for reaching out! We have safely received your technical service and repair quote inquiry.',
  customGreetingOnboarding: 'We are thrilled to begin working with you! Your onboarding project brief and specifications have been successfully recorded.',
};

// Module-level cached configuration (can be updated dynamically by Admin customizer)
let activeConfig: EmailTemplateConfig = { ...DEFAULT_EMAIL_CONFIG };

// Initialize from localStorage if present
if (typeof window !== 'undefined') {
  try {
    const saved = localStorage.getItem('rb_email_template_config');
    if (saved) {
      activeConfig = { ...DEFAULT_EMAIL_CONFIG, ...JSON.parse(saved) };
    }
  } catch {}
}

export function setActiveEmailConfig(config: Partial<EmailTemplateConfig>) {
  activeConfig = { ...activeConfig, ...config };
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('rb_email_template_config', JSON.stringify(activeConfig));
    } catch {}
  }
}

export function getActiveEmailConfig(): EmailTemplateConfig {
  return { ...activeConfig };
}

/**
 * Shared Email Layout Wrapper
 * Ensures 100% UI consistency across all transactional emails.
 */
function renderEmailShell({
  preheader,
  badgeText,
  badgeColor,
  badgeBg,
  badgeBorder,
  headline,
  subheadline,
  contentHtml,
  ctaText,
  ctaUrl,
  config = activeConfig,
}: {
  preheader: string;
  badgeText: string;
  badgeColor?: string;
  badgeBg?: string;
  badgeBorder?: string;
  headline: string;
  subheadline?: string;
  contentHtml: string;
  ctaText?: string;
  ctaUrl?: string;
  config?: Partial<EmailTemplateConfig>;
}): string {
  const currentYear = new Date().getFullYear();
  const cfg = { ...activeConfig, ...config };

  const accentColor = cfg.accentColor || '#f59e0b';
  const accentEnd = cfg.accentGradientEnd || '#d97706';
  const bgColor = cfg.backgroundColor || '#070709';
  const cardBg = cfg.cardBackground || '#0d1117';
  const effectiveBadgeColor = badgeColor || accentColor;
  const effectiveBadgeBg = badgeBg || 'rgba(245, 158, 11, 0.12)';
  const effectiveBadgeBorder = badgeBorder || 'rgba(245, 158, 11, 0.3)';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(headline)}</title>
  <style>
    /* Reset & base styles */
    body {
      margin: 0;
      padding: 24px 12px;
      background-color: ${bgColor};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #f1f5f9;
      -webkit-font-smoothing: antialiased;
      line-height: 1.6;
    }
    .wrapper {
      max-width: 600px;
      margin: 0 auto;
      background-color: ${cardBg};
      border: 1px solid #1e293b;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
    }
    .top-accent-line {
      height: 3px;
      background: linear-gradient(90deg, ${accentColor} 0%, #fbbf24 50%, ${accentEnd} 100%);
    }
    .header {
      background-color: #06080d;
      padding: 24px 24px 20px 24px;
      border-bottom: 1px solid #1e293b;
      text-align: left;
    }
    .brand-cluster {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }
    .brand-mark {
      display: inline-block;
      width: 32px;
      height: 32px;
      line-height: 32px;
      text-align: center;
      background-color: #161b22;
      border: 1px solid ${accentColor};
      border-radius: 8px;
      color: ${accentColor};
      font-weight: 800;
      font-family: monospace;
      font-size: 13px;
      letter-spacing: 0.5px;
      vertical-align: middle;
    }
    .brand-name {
      display: inline-block;
      vertical-align: middle;
      font-size: 16px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -0.2px;
      margin-left: 8px;
    }
    .brand-sub {
      display: block;
      font-size: 11px;
      font-family: monospace;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin-top: 2px;
      margin-left: 42px;
    }
    .header-title-box {
      margin-top: 14px;
    }
    .header-h1 {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -0.3px;
    }
    .header-sub {
      margin: 4px 0 0 0;
      font-size: 13px;
      color: #94a3b8;
    }
    .badge {
      display: inline-block;
      background-color: ${effectiveBadgeBg};
      border: 1px solid ${effectiveBadgeBorder};
      color: ${effectiveBadgeColor};
      font-family: monospace;
      font-size: 11px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 20px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 10px;
    }
    .body-content {
      padding: 24px;
    }
    .section-title {
      font-size: 11px;
      font-family: monospace;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin: 20px 0 10px 0;
      padding-bottom: 6px;
      border-bottom: 1px solid #1e293b;
    }
    .data-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      font-size: 13px;
    }
    .data-table tr {
      border-bottom: 1px solid #161f30;
    }
    .data-table td {
      padding: 9px 8px;
      vertical-align: top;
    }
    .data-table td.label {
      color: #94a3b8;
      font-weight: 600;
      width: 36%;
    }
    .data-table td.value {
      color: #f8fafc;
      font-weight: 500;
    }
    .box-snippet {
      background-color: #06080d;
      border: 1px solid #1e293b;
      border-left: 3px solid ${accentColor};
      border-radius: 8px;
      padding: 14px 16px;
      margin: 14px 0;
      color: #e2e8f0;
      font-size: 13px;
      line-height: 1.6;
      white-space: pre-wrap;
    }
    .sig-card {
      background-color: #06080d;
      border: 1px dashed #334155;
      border-radius: 10px;
      padding: 14px 16px;
      margin-top: 18px;
    }
    .contact-banner {
      background-color: #06080d;
      border: 1px solid #1e293b;
      border-radius: 10px;
      padding: 16px;
      margin-top: 24px;
      text-align: center;
    }
    .cta-btn {
      display: inline-block;
      background-color: ${accentColor};
      color: #000000;
      font-weight: 700;
      font-size: 12px;
      text-decoration: none;
      padding: 10px 20px;
      border-radius: 8px;
      margin-top: 10px;
    }
    .footer {
      background-color: #06080d;
      border-top: 1px solid #1e293b;
      padding: 20px 24px;
      text-align: center;
      font-size: 12px;
      color: #64748b;
      line-height: 1.6;
    }
    .footer a {
      color: #94a3b8;
      text-decoration: none;
    }
    .footer a:hover {
      color: ${accentColor};
    }
  </style>
</head>
<body>
  <!-- Preheader text hidden in inbox view -->
  <div style="display: none; max-height: 0px; overflow: hidden; mso-hide: all;">
    ${escapeHtml(preheader)}
  </div>

  <div class="wrapper">
    <div class="top-accent-line"></div>
    <div class="header">
      <div>
        <span class="brand-mark">${escapeHtml(cfg.brandMark)}</span>
        <span class="brand-name">${escapeHtml(cfg.brandName)}</span>
        <span class="brand-sub">${escapeHtml(cfg.brandSubtitle)}</span>
      </div>
      <div class="header-title-box">
        <h1 class="header-h1">${escapeHtml(headline)}</h1>
        ${subheadline ? `<p class="header-sub">${escapeHtml(subheadline)}</p>` : ''}
        ${badgeText ? `<span class="badge">${escapeHtml(badgeText)}</span>` : ''}
      </div>
    </div>

    <div class="body-content">
      ${contentHtml}

      ${
        ctaText && ctaUrl
          ? `<div class="contact-banner">
              <a href="${escapeHtml(ctaUrl)}" class="cta-btn">${escapeHtml(ctaText)}</a>
            </div>`
          : ''
      }
    </div>

    <div class="footer">
      <div>&copy; ${currentYear} ${escapeHtml(cfg.brandName)} &bull; All Rights Reserved.</div>
      <div style="margin-top: 4px;">
        <a href="mailto:${escapeHtml(cfg.supportEmail)}" style="color: ${accentColor};">${escapeHtml(cfg.supportEmail)}</a> &bull;
        <a href="tel:${escapeHtml(cfg.supportPhone.replace(/[^0-9]/g, ''))}" style="color: #38bdf8;">${escapeHtml(cfg.supportPhone)}</a> &bull;
        <a href="https://randonbrown.com">randonbrown.com</a>
      </div>
      <div style="margin-top: 4px; font-size: 11px; color: #475569;">
        ${escapeHtml(cfg.locationText)}
      </div>
      ${cfg.customFooterNote ? `<div style="margin-top: 6px; font-size: 11px; color: #94a3b8; font-style: italic;">${escapeHtml(cfg.customFooterNote)}</div>` : ''}
    </div>
  </div>
</body>
</html>`;
}

/* =========================================================================
   1. TECHNICAL SERVICE / REPAIR QUOTE TEMPLATES
   ========================================================================= */

// Admin Notification (Delivered to help@randonbrown.com)
export function generateAdminServiceQuoteEmail(data: ServiceQuoteInput, refId: string, config?: Partial<EmailTemplateConfig>) {
  const cfg = { ...activeConfig, ...config };
  const subject = `[New Request] Technical Service & Repair Quote - ${data.firstName} ${data.lastName} (#${refId.slice(0, 7)})`;
  
  const text = `A new technical service / repair quote request was submitted on randonbrown.com:

REFERENCE: #${refId}
DATE: ${data.signatureDate}

CLIENT CONTACT:
- Name: ${data.firstName} ${data.lastName}
- Email: ${data.email || 'Not provided'}
- Phone: ${data.phone || 'Not provided'}
- Location: ${data.location}, ${data.zipCode}
- Address: ${data.address || 'N/A'} (${data.addressType})
- Service Mode: ${data.serviceMode}
- Urgency: ${data.urgency} (Needed: ${data.whenNeeded || 'ASAP'})
- Availability: ${data.availability || 'Standard'}

PROBLEM DESCRIPTION:
${data.problem}

DIGITAL SIGNATURE:
- Signed By: ${data.clientSignature}
- Date: ${data.signatureDate}`;

  const contentHtml = `
    <div class="section-title">Client & Location Details</div>
    <table class="data-table">
      <tr><td class="label">Client Name:</td><td class="value"><strong>${escapeHtml(data.firstName)} ${escapeHtml(data.lastName)}</strong></td></tr>
      <tr><td class="label">Email Address:</td><td class="value"><a href="mailto:${escapeHtml(data.email)}" style="color: #38bdf8; text-decoration: none;">${escapeHtml(data.email || 'Not provided')}</a></td></tr>
      <tr><td class="label">Phone Number:</td><td class="value"><a href="tel:${escapeHtml(data.phone)}" style="color: #38bdf8; text-decoration: none;">${escapeHtml(data.phone || 'Not provided')}</a></td></tr>
      <tr><td class="label">Location / Zip:</td><td class="value">${escapeHtml(data.location)}, ${escapeHtml(data.zipCode)}</td></tr>
      <tr><td class="label">Physical Address:</td><td class="value">${escapeHtml(data.address || 'N/A')} (${escapeHtml(data.addressType)})</td></tr>
      <tr><td class="label">Service Mode:</td><td class="value" style="text-transform: capitalize;">${escapeHtml(data.serviceMode)}${data.serviceMode === 'pickup' ? ' <span style="color: #94a3b8; font-size: 12px;">(Meet to Pick Up)</span>' : data.serviceMode === 'onsite' ? ' <span style="color: #94a3b8; font-size: 12px;">(On-Site Service)</span>' : ''}</td></tr>
      <tr><td class="label">Urgency / Timeline:</td><td class="value">${escapeHtml(data.urgency)} (Needed: ${escapeHtml(data.whenNeeded || 'ASAP')})</td></tr>
      <tr><td class="label">Preferred Window:</td><td class="value">${escapeHtml(data.availability || 'Standard')}</td></tr>
    </table>

    <div class="section-title">Problem Description & Symptoms</div>
    <div class="box-snippet">${escapeHtml(data.problem)}</div>

    ${cfg.showSignatureCard ? `
    <div class="section-title">Digital Signature & Authorization</div>
    <div class="sig-card">
      <div style="font-size: 13px; color: #cbd5e1;">
        <strong>Authorized Signer:</strong> <span style="font-family: monospace; color: ${cfg.accentColor}; font-weight: bold;">${escapeHtml(data.clientSignature)}</span>
      </div>
      <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
        Signed on: ${escapeHtml(data.signatureDate)} &bull; IP & Timestamp Logged
      </div>
    </div>` : ''}
  `;

  const html = renderEmailShell({
    preheader: `New service quote from ${data.firstName} ${data.lastName}: ${data.problem.slice(0, 80)}`,
    headline: 'Technical Service / Repair Request',
    subheadline: 'An incoming client diagnostics and quote intake was submitted.',
    badgeText: `Reference: #${refId.slice(0, 8)}`,
    contentHtml,
    ctaText: cfg.ctaButtonTextQuote || `Reply Directly to ${data.firstName}`,
    ctaUrl: `mailto:${data.email || cfg.supportEmail}?subject=Re:%20Technical%20Service%20Quote%20(#${refId.slice(0, 8)})`,
    config: cfg
  });

  return { subject, text, html };
}

// Client Confirmation Receipt (Delivered to client's email)
export function generateClientServiceQuoteConfirmation(data: ServiceQuoteInput, refId: string, config?: Partial<EmailTemplateConfig>) {
  const cfg = { ...activeConfig, ...config };
  const subject = `We received your quote request — ${cfg.brandName} (#${refId.slice(0, 7)})`;

  const text = `Hi ${data.firstName},

${cfg.customGreetingQuote || 'Thank you for reaching out! We have received your technical service & repair quote request.'}

Reference ID: #${refId}

We are reviewing your details and will contact you directly to discuss diagnostics, parts evaluation, and scheduling.

SUMMARY OF YOUR SUBMISSION:
- Client Name: ${data.firstName} ${data.lastName}
- Contact Phone: ${data.phone || 'Not provided'}
- Service Mode: ${data.serviceMode}
- Urgency: ${data.urgency}
- Problem Summary:
${data.problem}

OUR "NO STICKER SHOCK" COMMITMENT:
Computer repairs can be unpredictable, which is why we are always upfront with our estimates. We only proceed with your authorization—if an issue turns out to be more complex than expected, we contact you first before doing any additional work.

If you have additional questions or photos of the equipment, simply reply directly to this email or reach us anytime at ${cfg.supportEmail}.

Best regards,
${cfg.brandName}
${cfg.brandSubtitle}
${cfg.supportEmail} | ${cfg.supportPhone} | https://randonbrown.com`;

  const contentHtml = `
    <p style="font-size: 15px; margin-top: 0;">Hi <strong>${escapeHtml(data.firstName)}</strong>,</p>
    <p style="color: #cbd5e1;">
      ${escapeHtml(cfg.customGreetingQuote || 'Thank you for reaching out! We have safely received your technical service and repair quote inquiry.')}
    </p>

    <div class="section-title">Summary of Your Request</div>
    <table class="data-table">
      <tr><td class="label">Service Mode:</td><td class="value" style="text-transform: capitalize;">${escapeHtml(data.serviceMode)}${data.serviceMode === 'pickup' ? '<div style="font-size: 12px; color: #94a3b8; margin-top: 4px;">&bull; Meet to Pick Up (Cash, check, or credit card via Zoho Payments upon service completion)</div>' : data.serviceMode === 'onsite' ? '<div style="font-size: 12px; color: #94a3b8; margin-top: 4px;">&bull; Visit On-Site (Cash, check, or credit card via Zoho Payments upon service completion)</div>' : ''}</td></tr>
      <tr><td class="label">Urgency:</td><td class="value">${escapeHtml(data.urgency)}</td></tr>
      <tr><td class="label">Location:</td><td class="value">${escapeHtml(data.location)}, ${escapeHtml(data.zipCode)}</td></tr>
      <tr><td class="label">Timeline Needed:</td><td class="value">${escapeHtml(data.whenNeeded || 'ASAP')}</td></tr>
    </table>

    <div class="section-title">Reported Hardware / Problem Summary</div>
    <div class="box-snippet">${escapeHtml(data.problem)}</div>

    <div style="margin: 20px 0; padding: 16px; border-radius: 12px; background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25);">
      <div style="font-size: 13px; font-weight: 700; color: #fbbf24; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
        &#128737;&#65039; &quot;No Sticker Shock&quot; Commitment
      </div>
      <div style="font-size: 13px; color: #cbd5e1; line-height: 1.5;">
        Computer repairs can be unpredictable, which is why we are always upfront with our estimates. We only proceed with your authorization&mdash;if an issue turns out to be more complex than expected, we contact you first before doing any additional work.
      </div>
    </div>

    <div class="contact-banner">
      <div style="font-size: 14px; font-weight: 700; color: #ffffff; margin-bottom: 6px;">
        Need to send photos or extra details?
      </div>
      <div style="font-size: 13px; color: #94a3b8;">
        Simply reply directly to this email or reach out anytime at <a href="mailto:${escapeHtml(cfg.supportEmail)}" style="color: ${cfg.accentColor};">${escapeHtml(cfg.supportEmail)}</a>.
      </div>
    </div>
  `;

  const html = renderEmailShell({
    preheader: `Thank you ${data.firstName}, we have received your service request #${refId.slice(0, 8)}.`,
    headline: 'Quote Request Received',
    subheadline: 'Your diagnostics intake is under review.',
    badgeText: `Reference: #${refId.slice(0, 8)}`,
    contentHtml,
    config: cfg
  });

  return { subject, text, html };
}

/* =========================================================================
   2. WEBSITE ONBOARDING INTAKE TEMPLATES
   ========================================================================= */

// Helper to format website layout options cleanly
function formatLayoutDisplayName(layoutKey?: string): string {
  switch (layoutKey) {
    case 'local_service':
    case 'classic_topbar':
      return 'Local Service & Contractor (Logo Left, Click-to-Call Nav, Estimate Form & Reviews Body, Local Footer)';
    case 'storefront_retail':
    case 'modern_centered':
      return 'Main Street Storefront & Retail (Centered Logo, Hours & Specials Nav, Products & Directions Body, Social Footer)';
    case 'professional_practice':
      return 'Professional Practice & Appointments (Credentials Header, Practice Areas & Team Bios Body, Consultation Footer)';
    case 'emergency_dispatch':
    case 'lead_engine':
      return 'Rapid Dispatch & Emergency Response (24/7 Hotline Header, Instant Dispatch Form Body, Coverage Radius Footer)';
    case 'custom_randon':
      return 'Let Randon Build a Custom Design (Custom Architecture Crafted by Randon)';
    case 'sidebar_nav':
      return 'Persistent Sidebar Nav (Left Logo & Nav Panel, Wide Fluid Body, Pinned Footer)';
    default:
      return layoutKey || 'Local Service & Contractor Architecture';
  }
}

// Admin Notification (Delivered to help@randonbrown.com)
export function generateAdminWebsiteOnboardingEmail(data: WebsiteOnboardingInput, refId: string, config?: Partial<EmailTemplateConfig>) {
  const cfg = { ...activeConfig, ...config };
  const pkgName = data.selectedPackage === 'package-a' 
    ? 'Package A: Hands-Off & Ultra-Fast ($2,000)' 
    : data.selectedPackage === 'package-b'
    ? 'Package B: Editable & Flexible ($4,000)'
    : 'Custom Web Build';

  const logoStatusText = data.needsLogo === 'need_logo'
    ? `Yes, Custom Logo Design Needed${data.logoNotes ? ` (Notes: ${data.logoNotes})` : ''}`
    : data.needsLogo === 'have_logo'
    ? 'Client Has Existing Logo (Will email / cloud link)'
    : data.needsLogo === 'text_only'
    ? 'No Logo Needed / Stylized Header Text Wordmark'
    : 'Not Specified';

  const layoutText = formatLayoutDisplayName(data.preferredLayout);
  const editingText = data.editingPreference === 'self_managed' ? 'Self-Managed / Edit Own Website (CMS)' : 'Complete Hands-Off (Randon Manages Updates @ $75/hr)';

  const subject = `[Website Onboarding] Project Brief - ${data.businessName || `${data.firstName} ${data.lastName}`} (#${refId.slice(0, 7)})`;

  const text = `A new website onboarding intake form was submitted on randonbrown.com:

REFERENCE: #${refId}
DATE: ${data.signatureDate}

BUSINESS & CONTACT:
- Business: ${data.businessName}
- Desired Domain: ${data.domainName} (${data.domainStatus === 'need_purchase' ? 'Needs to purchase domain' : 'Already owns domain elsewhere'})
- Hosting Preference: ${data.hostingPreference === 'client_controlled' ? 'Client Direct Control (Self-Hosted)' : 'Hosted & Managed by Randon (CDN Network Hosting • $75/hr Post-Launch Edits)'}
- Site Editing Preference: ${editingText}
- Contact: ${data.firstName} ${data.lastName}
- Phone: ${data.contactNumber}
- Email: ${data.email}
- Location: ${data.city}, ${data.zipCode}
- Tagline / Mission: ${data.tagline || 'N/A'}
- Hours: ${data.hours || 'N/A'}
- Address: ${data.address || 'N/A'}
- Socials: ${data.socialLinks || 'N/A'}

PACKAGE & ARCHITECTURE:
- Layout Architecture: ${layoutText}${data.layoutNotes ? ` (Notes: ${data.layoutNotes})` : ''}
- Logo Requirement: ${logoStatusText}
- Lead Routing: ${data.leadOption === 'no-form' ? 'Direct Contact Only (No Web Form)' : 'Contact Form on Website (Direct Inbox Forwarding)'}
${data.leadOption !== 'no-form' ? `- Form Fields: ${(data.formFields && data.formFields.length > 0) ? data.formFields.join(', ') : 'Standard (Name, Email, Phone, Message)'}${data.customFormFields ? ` (Notes: ${data.customFormFields})` : ''}
- Monthly Submissions Needed: ${data.monthlySubmissions || 'Up to 250 / month'}\n` : ''}- GitHub Account: ${data.githubUsername ? `@${data.githubUsername.replace(/^@/, '')}` : 'To be set up during staging'}
- Core Pages: ${data.corePages || 'Home, About, Services, Contact'}
- Brand Colors: ${data.brandColors || 'N/A'}
- Shared Cloud Assets: ${data.cloudFolderLink || 'None provided (attaching via email)'}

DIGITAL SIGNATURE:
- Signed By: ${data.clientSignature}
- Date: ${data.signatureDate}`;

  const contentHtml = `
    <div class="section-title">Business & Client Contact</div>
    <table class="data-table">
      <tr><td class="label">Business Name:</td><td class="value"><strong>${escapeHtml(data.businessName)}</strong></td></tr>
      <tr><td class="label">Desired Domain:</td><td class="value" style="color: #38bdf8; font-weight: 700;">${escapeHtml(data.domainName)} <span style="font-size: 11px; color: #94a3b8; font-weight: normal;">(${data.domainStatus === 'need_purchase' ? 'Needs to purchase domain' : 'Already owns domain'})</span></td></tr>
      <tr><td class="label">Hosting Preference:</td><td class="value" style="color: #10b981; font-weight: 600;">${data.hostingPreference === 'client_controlled' ? 'Client Direct Control (Self-Hosted)' : 'Hosted & Managed by Randon (CDN Network Hosting • $75/hr Post-Launch Edits)'}</td></tr>
      <tr><td class="label">Site Editing Preference:</td><td class="value" style="color: #fbbf24; font-weight: 600;">${escapeHtml(editingText)}</td></tr>
      <tr><td class="label">Contact Person:</td><td class="value">${escapeHtml(data.firstName)} ${escapeHtml(data.lastName)}</td></tr>
      <tr><td class="label">Contact Email:</td><td class="value"><a href="mailto:${escapeHtml(data.email)}" style="color: #38bdf8; text-decoration: none;">${escapeHtml(data.email)}</a></td></tr>
      <tr><td class="label">Contact Phone:</td><td class="value"><a href="tel:${escapeHtml(data.contactNumber)}" style="color: #38bdf8; text-decoration: none;">${escapeHtml(data.contactNumber)}</a></td></tr>
      <tr><td class="label">City & Zip:</td><td class="value">${escapeHtml(data.city)}, ${escapeHtml(data.zipCode)}</td></tr>
      ${data.tagline ? `<tr><td class="label">Tagline / Mission:</td><td class="value">${escapeHtml(data.tagline)}</td></tr>` : ''}
    </table>

    <div class="section-title">Package & Technical Scope</div>
    <div class="box-snippet">
      <div style="font-size: 14px; color: #ffffff; font-weight: 700; margin-bottom: 6px;">
        Package: ${escapeHtml(pkgName)}
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Layout Architecture:</strong> <span style="color: #38bdf8;">${escapeHtml(layoutText)}</span>
        ${data.layoutNotes ? `<div style="font-size: 12px; color: #94a3b8; margin-top: 2px;">Layout Notes: ${escapeHtml(data.layoutNotes)}</div>` : ''}
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Logo Requirement:</strong> <span style="color: #fbbf24;">${escapeHtml(logoStatusText)}</span>
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Lead Routing:</strong> ${data.leadOption === 'no-form' ? "Direct Contact Only (No Web Form)" : "Contact Form on Website (Direct Inbox Forwarding)"}
      </div>
      ${data.leadOption !== 'no-form' ? `
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Form Fields Requested:</strong> <span style="color: #38bdf8;">${escapeHtml((data.formFields && data.formFields.length > 0) ? data.formFields.join(', ') : 'Standard (Name, Email, Phone, Message)')}</span>
        ${data.customFormFields ? `<div style="font-size: 12px; color: #94a3b8; margin-top: 2px;">Field Notes: ${escapeHtml(data.customFormFields)}</div>` : ''}
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Expected Monthly Submissions:</strong> <span style="color: #34d399;">${escapeHtml(data.monthlySubmissions || 'Up to 250 / month')}</span>
      </div>` : ''}
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>GitHub Account:</strong> ${data.githubUsername ? `<span style="color: #38bdf8; font-family: monospace;">@${escapeHtml(data.githubUsername.replace(/^@/, ''))}</span>` : 'To be set up during staging'}
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Core Pages:</strong> ${escapeHtml(data.corePages)}
      </div>
      <div style="font-size: 13px; color: #cbd5e1; margin-bottom: 4px;">
        <strong>Brand Colors:</strong> ${escapeHtml(data.brandColors || 'N/A')}
      </div>
      <div style="font-size: 13px; color: #cbd5e1;">
        <strong>Shared Assets Link:</strong> ${data.cloudFolderLink ? `<a href="${escapeHtml(data.cloudFolderLink)}" style="color: #38bdf8;">${escapeHtml(data.cloudFolderLink)}</a>` : 'None provided'}
      </div>
    </div>

    ${cfg.showSignatureCard ? `
    <div class="section-title">Digital Signature & Authorization</div>
    <div class="sig-card">
      <div style="font-size: 13px; color: #cbd5e1;">
        <strong>Authorized Signer:</strong> <span style="font-family: monospace; color: ${cfg.accentColor}; font-weight: bold;">${escapeHtml(data.clientSignature)}</span>
      </div>
      <div style="font-size: 12px; color: #64748b; margin-top: 4px;">
        Signed on: ${escapeHtml(data.signatureDate)} &bull; IP & Timestamp Logged
      </div>
    </div>` : ''}
  `;

  const html = renderEmailShell({
    preheader: `New website onboarding brief for ${data.businessName} (${data.domainName})`,
    headline: 'Website Project Intake Brief',
    subheadline: 'A comprehensive onboarding specification was submitted.',
    badgeText: `Brief: #${refId.slice(0, 8)}`,
    contentHtml,
    ctaText: cfg.ctaButtonTextOnboarding || `Reply to ${data.firstName}`,
    ctaUrl: `mailto:${data.email}?subject=Re:%20Website%20Project%20Brief%20-%20${encodeURIComponent(data.businessName)}`,
    config: cfg
  });

  return { subject, text, html };
}

// Client Confirmation Receipt (Delivered to client's email)
export function generateClientWebsiteOnboardingConfirmation(data: WebsiteOnboardingInput, refId: string, config?: Partial<EmailTemplateConfig>) {
  const cfg = { ...activeConfig, ...config };

  const logoStatusText = data.needsLogo === 'need_logo'
    ? 'Yes, Custom Logo Design Requested'
    : data.needsLogo === 'have_logo'
    ? 'Existing Logo Provided'
    : data.needsLogo === 'text_only'
    ? 'No Logo Needed / Stylized Header Text Wordmark'
    : 'Not Specified';

  const layoutText = formatLayoutDisplayName(data.preferredLayout);
  const hostingText = data.hostingPreference === 'client_controlled' ? 'Client Direct Control (Self-Hosted)' : 'Hosted & Managed by Randon (CDN Network Hosting • $75/hr Edits)';
  const editingText = data.editingPreference === 'self_managed' ? 'Self-Managed / Edit Own Website (CMS)' : 'Complete Hands-Off (Randon Manages Updates @ $75/hr)';

  const subject = `Welcome to your website project onboarding — ${cfg.brandName} (#${refId.slice(0, 7)})`;

  const text = `Hi ${data.firstName},

${cfg.customGreetingOnboarding || 'We are thrilled to begin working with you! Your project brief and specifications have been successfully recorded.'}

Project Reference ID: #${refId}

WHAT HAPPENS NEXT:
1. Intake Review: We analyze your core pages, domain requirements, and brand preferences.
2. Asset Staging: We organize your logos, images, and content from your provided cloud repository or email attachments.
3. Project Start: We will reach out directly to begin the development build process.

SUMMARY OF SPECIFICATIONS:
- Business Name: ${data.businessName}
- Desired Domain: ${data.domainName}
- Hosting Setup: ${hostingText}
- Site Editing Style: ${editingText}
- Layout Architecture: ${layoutText}${data.layoutNotes ? ` (Notes: ${data.layoutNotes})` : ''}
- Logo Requirement: ${logoStatusText}
- Core Pages: ${data.corePages}

If you have additional assets or branding files to send, simply reply directly to this email or reach us anytime at ${cfg.supportEmail}.

Best regards,
${cfg.brandName}
${cfg.brandSubtitle}
${cfg.supportEmail} | ${cfg.supportPhone} | https://randonbrown.com`;

  const contentHtml = `
    <p style="font-size: 15px; margin-top: 0;">Hi <strong>${escapeHtml(data.firstName)}</strong>,</p>
    <p style="color: #cbd5e1;">
      ${escapeHtml(cfg.customGreetingOnboarding || `We are thrilled to begin working with you on ${data.businessName}! Your onboarding brief and specifications have been successfully recorded.`)}
    </p>

    <div class="section-title">What Happens Next</div>
    <div style="background-color: #06080d; border: 1px solid #1e293b; border-radius: 8px; padding: 16px; margin: 14px 0;">
      <div style="margin-bottom: 10px; font-size: 13px; color: #cbd5e1;">
        <span style="display: inline-block; width: 20px; height: 20px; border-radius: 50%; background-color: ${cfg.accentColor}; color: #000; font-weight: 800; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">1</span>
        <strong>Intake Review:</strong> We analyze your core pages, domain requirements, and brand preferences.
      </div>
      <div style="margin-bottom: 10px; font-size: 13px; color: #cbd5e1;">
        <span style="display: inline-block; width: 20px; height: 20px; border-radius: 50%; background-color: ${cfg.accentColor}; color: #000; font-weight: 800; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">2</span>
        <strong>Asset Staging:</strong> We organize your logos, images, and content from your cloud repository or email attachments.
      </div>
      <div style="font-size: 13px; color: #cbd5e1;">
        <span style="display: inline-block; width: 20px; height: 20px; border-radius: 50%; background-color: ${cfg.accentColor}; color: #000; font-weight: 800; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">3</span>
        <strong>Project Start:</strong> We will reach out directly to begin the build process.
      </div>
    </div>

    <div class="section-title">Summary of Your Specifications</div>
    <table class="data-table">
      <tr><td class="label">Business Name:</td><td class="value"><strong>${escapeHtml(data.businessName)}</strong></td></tr>
      <tr><td class="label">Desired Domain:</td><td class="value" style="color: #38bdf8; font-weight: 700;">${escapeHtml(data.domainName)}</td></tr>
      <tr><td class="label">Hosting Setup:</td><td class="value" style="color: #10b981; font-weight: 600;">${escapeHtml(hostingText)}</td></tr>
      <tr><td class="label">Editing Preference:</td><td class="value" style="color: #fbbf24; font-weight: 600;">${escapeHtml(editingText)}</td></tr>
      <tr><td class="label">Layout Style:</td><td class="value" style="color: #38bdf8; font-weight: 600;">${escapeHtml(layoutText)}${data.layoutNotes ? `<div style="font-size: 11px; color: #94a3b8; font-weight: normal; margin-top: 3px;">Notes: ${escapeHtml(data.layoutNotes)}</div>` : ''}</td></tr>
      <tr><td class="label">Logo Requirement:</td><td class="value" style="color: #fbbf24;">${escapeHtml(logoStatusText)}</td></tr>
      <tr><td class="label">Core Pages:</td><td class="value">${escapeHtml(data.corePages)}</td></tr>
      <tr><td class="label">Authorized Signer:</td><td class="value">${escapeHtml(data.clientSignature)}</td></tr>
    </table>

    <div class="contact-banner">
      <div style="font-size: 14px; font-weight: 700; color: #ffffff; margin-bottom: 6px;">
        Have additional assets or branding files to send?
      </div>
      <div style="font-size: 13px; color: #94a3b8;">
        Simply reply directly to this email or contact us at <a href="mailto:${escapeHtml(cfg.supportEmail)}" style="color: ${cfg.accentColor};">${escapeHtml(cfg.supportEmail)}</a>.
      </div>
    </div>
  `;

  const html = renderEmailShell({
    preheader: `Congratulations ${data.firstName}, your website project brief for ${data.businessName} has been received.`,
    headline: 'Project Brief Received',
    subheadline: 'Your specifications and onboarding details have been recorded.',
    badgeText: `Brief: #${refId.slice(0, 8)}`,
    contentHtml,
    config: cfg
  });

  return { subject, text, html };
}

/* =========================================================================
   3. ADMIN SECURITY TWO-FACTOR OTP EMAIL
   ========================================================================= */

export function generateAdminOtpEmail(toEmail: string, otpCode: string, config?: Partial<EmailTemplateConfig>) {
  const cfg = { ...activeConfig, ...config };
  const subject = `🔐 Admin 2FA Security Passcode: ${otpCode} — ${cfg.brandName} Portal`;

  const text = `${cfg.brandName} Admin Security 2FA Verification:

Your one-time security passcode is: ${otpCode}

This code is valid for 5 minutes.
Account: ${toEmail}
Timestamp: ${new Date().toLocaleString()} (Central Time)

If you did not initiate this request, your administrative database remains locked.`;

  const contentHtml = `
    <div style="text-align: center; padding: 10px 0 20px 0;">
      <p style="font-size: 14px; color: #d1d5db; line-height: 1.6; margin: 0 0 20px 0;">
        A sign-in request to your <strong>Admin Dashboard & Database Inbox</strong> was initiated for administrator account:
        <br><strong style="color: #ffffff; font-family: monospace;">${escapeHtml(toEmail)}</strong>
      </p>

      <div style="display: inline-block; background-color: #06080d; border: 2px dashed ${cfg.accentColor}; border-radius: 12px; padding: 16px 36px; margin: 10px 0 16px 0;">
        <span style="font-size: 34px; font-weight: 900; letter-spacing: 8px; color: ${cfg.accentColor}; font-family: 'Courier New', monospace;">
          ${escapeHtml(otpCode)}
        </span>
      </div>

      <p style="font-size: 12px; color: #94a3b8; margin: 12px 0 0 0;">
        ⏳ This passcode is valid for <strong>5 minutes</strong>. Do not share this code.
      </p>

      <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #1e293b; font-size: 11px; color: #64748b; font-family: monospace;">
        Timestamp: ${escapeHtml(new Date().toLocaleString())} &bull; Security Logged
      </div>
    </div>
  `;

  const html = renderEmailShell({
    preheader: `Your Admin 2FA Passcode is ${otpCode}. Valid for 5 minutes.`,
    headline: 'Administrator Security Verification',
    subheadline: 'Two-Factor Authentication One-Time Passcode (OTP)',
    badgeText: 'Admin 2FA Security',
    contentHtml,
    config: cfg
  });

  return { subject, text, html };
}

/* =========================================================================
   4. REALISTIC SAMPLE PREVIEW DATA
   ========================================================================= */

export const SAMPLE_SERVICE_QUOTE: ServiceQuoteInput = {
  firstName: "Jane",
  lastName: "Doe",
  email: "jane.doe@example.com",
  phone: "(573) 555-0149",
  location: "Houston, MO",
  zipCode: "65483",
  addressType: "business",
  address: "104 Main Street, Suite B",
  serviceMode: "onsite",
  availability: "Weekdays 9:00 AM - 1:00 PM",
  urgency: "Urgent (Within 24 Hours)",
  whenNeeded: "Tomorrow Morning",
  problem: "Core Cisco switch in our main communications closet lost PoE connectivity. Multiple VoIP handsets and 3 access points dropped offline. Fan lights are amber.",
  clientSignature: "Jane Doe",
  signatureDate: "2026-09-25 15:30:00"
};

export const SAMPLE_WEBSITE_ONBOARDING: WebsiteOnboardingInput = {
  firstName: "Michael",
  lastName: "Sterling",
  contactNumber: "(573) 555-0182",
  email: "sterling@ozarkoutfitters.com",
  city: "Licking",
  zipCode: "65542",
  businessName: "Ozark Outfitters & Tactical Gear",
  domainName: "ozarkoutfitters.com",
  tagline: "Rugged gear engineered for the Missouri wild",
  hours: "Mon-Sat: 8:00 AM - 6:00 PM",
  address: "412 West Commercial Ave, Licking, MO",
  socialLinks: "facebook.com/ozarkoutfitters",
  corePages: "Home, Retail Inventory, Firearms & Archery, Guided Trips, Contact & Location",
  brandColors: "Forest Green (#1b4332), Amber Gold (#f59e0b), Dark Slate (#111827)",
  cloudFolderLink: "https://drive.google.com/drive/folders/sample-brand-assets",
  leadOption: "email",
  selectedPackage: "package-a",
  hostingPreference: "randon_managed",
  needsLogo: "need_logo",
  logoNotes: "Want an antler silhouette crossed with a compass mark",
  stagedFileNames: ["storefront_hero.jpg", "field_team_photo.png", "tax_id_form.pdf"],
  clientSignature: "Michael Sterling",
  signatureDate: "2026-09-25 16:45:00"
};

